import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseClient';
import './LessonBrowser.css';

const LessonBrowser = () => {
    const { isDarkTheme } = useTheme();
    const navigate = useNavigate();

    const [units, setUnits] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lessonsLoading, setLessonsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchUnits = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('units')
                    .select('id, title, description')
                    .order('title', { ascending: true });
                if (error) throw error;
                setUnits(data || []);
            } catch (err) {
                console.error('[LessonBrowser] Units error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUnits();
    }, []);

    const fetchLessons = useCallback(async (unitId) => {
        setLessonsLoading(true);
        try {
            let query = supabase
                .from('lessons')
                .select('id, title, tags, front_content, status, created_at')
                .order('created_at', { ascending: false });

            if (unitId) query = query.eq('unit_id', unitId);
            if (searchQuery.trim()) query = query.ilike('title', `%${searchQuery.trim()}%`);

            const { data, error } = await query.eq('status', 'approved').limit(100);
            if (error) throw error;
            setLessons(data || []);
        } catch (err) {
            console.error('[LessonBrowser] Lessons error:', err);
            // fallback without approved filter
            try {
                let query = supabase
                    .from('lessons')
                    .select('id, title, tags, front_content, status, created_at')
                    .order('created_at', { ascending: false });
                if (unitId) query = query.eq('unit_id', unitId);
                if (searchQuery.trim()) query = query.ilike('title', `%${searchQuery.trim()}%`);
                const { data } = await query.limit(100);
                setLessons(data || []);
            } catch (fallbackErr) {
                console.error('[LessonBrowser] Lessons fallback error:', fallbackErr);
            }
        } finally {
            setLessonsLoading(false);
        }
    }, [searchQuery]);

    useEffect(() => {
        if (selectedUnit) {
            fetchLessons(selectedUnit.id);
        }
    }, [selectedUnit, fetchLessons]);

    const handleUnitClick = (unit) => {
        setSelectedUnit(unit);
        setSearchQuery('');
    };

    const handleBackToUnits = () => {
        setSelectedUnit(null);
        setLessons([]);
        setSearchQuery('');
    };

    const handleBrowseAll = () => {
        setSelectedUnit({ id: null, title: 'All Lessons', description: 'Browse all available lessons' });
        fetchLessons(null);
    };

    return (
        <div className={`lesson-browser ${isDarkTheme ? 'dark' : 'light'}`}>
            <div className="lesson-browser-container">
                {!selectedUnit ? (
                    <>
                        <div className="lesson-browser-header">
                            <div>
                                <h1>Lessons</h1>
                                <p className="lesson-browser-subtitle">Choose a unit to start learning</p>
                            </div>
                            <button className="lesson-browser-all-btn" onClick={handleBrowseAll}>
                                Browse All Lessons
                            </button>
                        </div>

                        {loading ? (
                            <div className="lesson-browser-loading">Loading units...</div>
                        ) : units.length === 0 ? (
                            <div className="lesson-browser-empty">
                                <div className="lesson-browser-empty-icon">📚</div>
                                <h3>No units available yet</h3>
                                <p>Check back soon — new lessons are being added.</p>
                            </div>
                        ) : (
                            <div className="lesson-units-grid">
                                {units.map((unit, index) => (
                                    <div
                                        key={unit.id}
                                        className="lesson-unit-card card"
                                        onClick={() => handleUnitClick(unit)}
                                    >
                                        <div className="lesson-unit-number">Unit {index + 1}</div>
                                        <h3 className="lesson-unit-title">{unit.title}</h3>
                                        {unit.description && (
                                            <p className="lesson-unit-desc">{unit.description}</p>
                                        )}
                                        <div className="lesson-unit-arrow">→</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="lesson-browser-header">
                            <div>
                                <button className="lesson-browser-back-btn" onClick={handleBackToUnits}>
                                    ← Back to Units
                                </button>
                                <h1>{selectedUnit.title}</h1>
                                {selectedUnit.description && (
                                    <p className="lesson-browser-subtitle">{selectedUnit.description}</p>
                                )}
                            </div>
                        </div>

                        <div className="lesson-browser-filters">
                            <div className="lesson-browser-search">
                                <span className="lesson-search-icon">🔍</span>
                                <input
                                    type="text"
                                    placeholder="Search lessons..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        {lessonsLoading ? (
                            <div className="lesson-browser-loading">Loading lessons...</div>
                        ) : lessons.length === 0 ? (
                            <div className="lesson-browser-empty">
                                <div className="lesson-browser-empty-icon">🧠</div>
                                <h3>No lessons found</h3>
                                <p>Try adjusting your search or check back later.</p>
                            </div>
                        ) : (
                            <div className="lesson-list">
                                {lessons.map((lesson, index) => (
                                    <div
                                        key={lesson.id}
                                        className="lesson-item card"
                                        onClick={() => navigate(`/learn/lesson/${lesson.id}`)}
                                    >
                                        <div className="lesson-item-left">
                                            <span className="lesson-item-index">{index + 1}</span>
                                            <div className="lesson-item-info">
                                                <div className="lesson-item-title">{lesson.title || 'Untitled Lesson'}</div>
                                                <div className="lesson-item-meta">
                                                    <span className="lesson-type-badge">📖 lesson</span>
                                                    {Array.isArray(lesson.tags) && lesson.tags.slice(0, 3).map((tag, i) => (
                                                        <span key={i} className="lesson-tag">{tag}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="lesson-item-right">
                                            <span className="lesson-open-btn">Open →</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default LessonBrowser;
